import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import AuthHero from '../components/AuthHero';
import Logo from '../components/Logo';
import { PasswordInput } from '../components/ui';

const FIELDS = [
  ['fullName', 'Full name', 'text'],
  ['email', 'Email', 'email'],
  ['phoneNumber', 'Phone number', 'tel'],
  ['password', 'Password', 'password'],
  ['confirmPassword', 'Confirm password', 'password'],
];

export default function Register() {
  const [form, setForm] = useState({
    fullName: '', email: '', phoneNumber: '', password: '', confirmPassword: ''
  });
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [loading, setLoading] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  // Two steps: 'form' (details) → 'code' (enter the 6-digit code we emailed).
  const [step, setStep] = useState('form');
  const [code, setCode] = useState('');
  const [cooldown, setCooldown] = useState(0);
  const { register } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const refCode = searchParams.get('ref') || '';

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const sendCode = async () => {
    const res = await api.post('/auth/email-code/signup', { email: form.email.trim() });
    setInfo(res.data?.message || `We sent a 6-digit code to ${form.email}.`);
    setCooldown(60);
  };

  // Step 1: check the form, then email a code to verify the address.
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (form.password !== form.confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (!acceptTerms) {
      setError('You must agree to the Terms & Legal Disclaimer to register');
      return;
    }
    setLoading(true);
    try {
      await sendCode();
      setCode('');
      setStep('code');
    } catch (err) {
      setError(err.response?.data?.message || 'Could not send the code');
    } finally {
      setLoading(false);
    }
  };

  // Step 2: create the account with the emailed code.
  const handleVerify = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await register({
        fullName: form.fullName,
        email: form.email,
        phoneNumber: form.phoneNumber,
        password: form.password,
        emailCode: code.trim(),
      });
      navigate('/signals');
    } catch (err) {
      setError(err.response?.data?.message || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const resend = async () => {
    setError('');
    try { await sendCode(); } catch (err) { setError(err.response?.data?.message || 'Could not send the code'); }
  };

  // Admin can close new sign-ups — someone opening /register directly (old link,
  // referral link) gets a clear message instead of a form the server would refuse.
  const [registrationOpen, setRegistrationOpen] = useState(true);
  useEffect(() => {
    api.get('/auth/registration-status')
      .then((r) => setRegistrationOpen(r.data?.enabled !== false))
      .catch(() => {});
  }, []);

  if (!registrationOpen) {
    return (
      <div className="flex min-h-[calc(100vh-3.5rem)] flex-col items-center justify-center py-8 sm:py-12">
        <div className="mb-6 flex items-center gap-2.5">
          <Logo size={26} />
          <span className="font-display text-lg font-semibold tracking-tight text-slate-900">StockXignal</span>
        </div>
        <div className="w-full max-w-sm rounded-2xl border border-slate-200 bg-white px-6 py-7 text-center shadow-card">
          <h1 className="font-display text-lg font-semibold tracking-tight text-slate-900">Registration closed</h1>
          <p className="mt-2 text-sm text-slate-500">New sign-ups aren&apos;t open right now. Existing members can sign in as usual.</p>
          <Link to="/login" className="fs-btn-primary mt-5 inline-block w-full py-2.5 text-sm">Sign in</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-[calc(100vh-3.5rem)] flex-col items-center justify-center py-8 sm:py-12">
      <div className="mb-6 flex items-center gap-2.5 lg:hidden">
        <Logo size={26} />
        <span className="font-display text-lg font-semibold tracking-tight text-slate-900">StockXignal</span>
      </div>

      <div className="w-full max-w-4xl overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-card lg:grid lg:grid-cols-2">
        <AuthHero
          headline="Start your 7-day free trial."
          points={[
            'Live signals on stocks and options',
            'Stop-loss and target tracking',
            'Cancel anytime',
          ]}
        />

        <div className="flex flex-col justify-center px-6 py-10 sm:px-10 sm:py-12">
          <div className="mx-auto w-full max-w-sm">
            <h1 className="font-display text-2xl font-semibold tracking-tight text-slate-900">
              {step === 'code' ? 'Check your email' : 'Create your account'}
            </h1>
            <p className="mt-1.5 text-sm text-slate-500">
              {step === 'code' ? 'Enter the 6-digit code to verify your email.' : '7-day free trial, then choose a plan.'}
            </p>

            {error && (
              <div className="mt-5 rounded-xl border border-rose-100 bg-rose-50 px-3.5 py-2.5 text-sm text-rose-700">
                {error}
              </div>
            )}

            {step === 'code' ? (
              <form onSubmit={handleVerify} className="mt-6 space-y-3.5">
                {info && (
                  <p className="rounded-xl border border-emerald-100 bg-emerald-50 px-3.5 py-2.5 text-sm text-emerald-700">{info}</p>
                )}
                <div>
                  <label className="mb-1.5 block text-xs font-medium text-slate-600">Verification code</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={6}
                    required
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                    className="fs-input text-center font-mono text-xl tracking-[0.5em]"
                    placeholder="••••••"
                  />
                  <p className="mt-1.5 text-[11px] text-slate-400">Code expires in 15 minutes. Check spam if you don't see it.</p>
                </div>
                <button type="submit" disabled={loading || code.length !== 6} className="fs-btn-primary w-full py-3 text-sm">
                  {loading ? 'Creating…' : 'Verify & create account'}
                </button>
                <div className="flex items-center justify-between text-xs">
                  <button type="button" onClick={() => { setStep('form'); setError(''); }} className="font-medium text-slate-500 hover:text-slate-800">
                    ← Change email
                  </button>
                  <button type="button" onClick={resend} disabled={cooldown > 0} className="font-medium text-brand-700 hover:underline disabled:text-slate-400 disabled:no-underline">
                    {cooldown > 0 ? `Resend code in ${cooldown}s` : 'Resend code'}
                  </button>
                </div>
              </form>
            ) : (
            <>
            {refCode ? (
              <p className="mt-5 rounded-lg border border-emerald-100 bg-emerald-50 px-3 py-2 text-xs text-emerald-700">
                Invited via referral: <strong>{refCode}</strong>
              </p>
            ) : null}

            <form onSubmit={handleSubmit} className="mt-6 space-y-3.5">
              {FIELDS.map(([key, label, type]) => (
                <div key={key}>
                  <label className="mb-1.5 block text-xs font-medium text-slate-600">{label}</label>
                  {type === 'password' ? (
                    <PasswordInput
                      required
                      autoComplete="new-password"
                      value={form[key]}
                      onChange={e => set(key, e.target.value)}
                    />
                  ) : (
                    <input
                      type={type}
                      required
                      value={form[key]}
                      onChange={e => set(key, e.target.value)}
                      className="fs-input"
                    />
                  )}
                </div>
              ))}

              <label className="flex items-start gap-2 pt-1 text-xs leading-relaxed text-slate-600 select-none">
                <input
                  type="checkbox"
                  checked={acceptTerms}
                  onChange={e => setAcceptTerms(e.target.checked)}
                  className="mt-0.5 h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500/30"
                />
                <span>
                  I agree to the{' '}
                  <Link to="/legal" className="underline text-brand-700 hover:text-brand-800">Terms &amp; Legal Disclaimer</Link>.
                </span>
              </label>

              <button
                type="submit"
                disabled={loading}
                className="fs-btn-primary w-full py-3 text-sm"
              >
                {loading ? 'Sending code…' : 'Continue'}
              </button>
            </form>
            </>
            )}

            <p className="mt-6 text-center text-sm text-slate-500">
              Already have an account?{' '}
              <Link to="/login" className="font-medium text-brand-700 hover:underline">Sign in</Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
